import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import AdminDetailDrawer, { DrawerFacts, DrawerSection } from "./AdminDetailDrawer";
import AdminListPagination from "./AdminListPagination";

/* ─────────────────────────────────────────────────────────────────
   Activity Log + Error Log (backed by activity_logs / error_logs).
   The business Audit Logs tab (old → new values) stays separate.
   ───────────────────────────────────────────────────────────────── */

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const ROLES = ["admin", "customer", "artist", "agency", "corporate", "onboarding_manager"];
const SOURCE_LABEL = { backend: "Backend", "backend.log": "Server log", frontend: "Browser" };

function Json({ value }) {
  if (value === null || value === undefined || (typeof value === "object" && Object.keys(value).length === 0)) {
    return <p className="text-muted fs-12">—</p>;
  }
  return (
    <pre className="fs-11" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 320, overflow: "auto", margin: 0 }}>
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function OutcomePill({ row }) {
  return (
    <span className={`pill ${row.outcome === "success" ? "pill-green" : "pill-red"}`} style={{ fontSize: 10 }}>
      {row.status} {row.outcome === "success" ? "OK" : "Failed"}
    </span>
  );
}

function ActivityRow({ row, onOpen, showActor = true }) {
  return (
    <tr onClick={() => onOpen(row)} style={{ cursor: "pointer" }} data-testid={`activity-row-${row.id}`}>
      <td className="fs-11 text-muted" style={{ whiteSpace: "nowrap" }}>{fmtTime(row.ts || row.at)}</td>
      {showActor && (
        <td className="fs-12">
          {row.actor_email || (row.actor_id ? row.actor_id.slice(0, 8) : <span className="text-muted">anonymous</span>)}
          {row.actor_role && <div className="fs-11 text-muted">{row.actor_role}</div>}
        </td>
      )}
      <td>
        <div className="fs-12">{row.action}</div>
        <div className="font-mono fs-11 text-muted">{row.method} {row.path}</div>
      </td>
      <td className="fs-12">
        {row.entity || "—"}
        {row.entity_id && <div className="fs-11 text-muted">{row.entity_id.slice(0, 18)}</div>}
      </td>
      <td><OutcomePill row={row} /></td>
      <td className="fs-11 text-muted">{row.ip || "—"}</td>
    </tr>
  );
}

function ActivityDetailDrawer({ row, onClose }) {
  return (
    <AdminDetailDrawer
      open={!!row}
      onClose={onClose}
      title={row?.action}
      subtitle={row ? `${row.method} ${row.path}` : ""}
      badge={row && <OutcomePill row={row} />}
      testId="activity-drawer"
    >
      {row && (
        <>
          <DrawerSection title="Who">
            <DrawerFacts items={[
              ["User", row.actor_email || "anonymous"],
              ["Role", row.actor_role],
              ["User ID", row.actor_id],
              ["IP", row.ip],
              ["Device", row.user_agent],
            ]} />
          </DrawerSection>
          <DrawerSection title="What">
            <DrawerFacts items={[
              ["Time", fmtTime(row.ts || row.at)],
              ["Target", row.entity],
              ["Target ID", row.entity_id],
              ["Status", row.status],
              ["Duration", row.duration_ms != null ? `${row.duration_ms} ms` : null],
              ["Request ID", row.request_id],
            ]} />
          </DrawerSection>
          {row.query && (
            <DrawerSection title="Query"><Json value={row.query} /></DrawerSection>
          )}
          <DrawerSection title="Data sent (secrets removed)"><Json value={row.body} /></DrawerSection>
        </>
      )}
    </AdminDetailDrawer>
  );
}

const EMPTY_ACTIVITY_FILTERS = { actor: "", role: "", outcome: "", method: "", entity: "", q: "", start: "", end: "" };

export function AdminActivityLog() {
  const [filters, setFilters] = useState(EMPTY_ACTIVITY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_ACTIVITY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, entities: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const params = useCallback((extra = {}) => {
    const p = new URLSearchParams(extra);
    Object.entries(applied).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [applied]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(`/admin/activity-logs?${params({ page, page_size: pageSize })}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params, page, pageSize]);

  const apply = (next = filters) => { setApplied(next); setPage(1); };
  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });

  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/activity-logs/export.csv?${params()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "activity-log.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div className="card" data-testid="admin-activity-log">
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <div>
          <div className="card-title"> Activity Log</div>
          <div className="fs-12 text-muted">Every change made on the platform: who did it, what they changed, and whether it worked.</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={exportCsv} data-testid="activity-export">Export CSV</button>
      </div>
      <form
        className="flex gap-8 pad-16"
        style={{ flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        onSubmit={(e) => { e.preventDefault(); apply(); }}
      >
        <input className="input" placeholder="User email or ID" value={filters.actor} onChange={set("actor")} style={{ width: 190 }} data-testid="activity-filter-actor" />
        <input className="input" placeholder="Search action, path, request ID" value={filters.q} onChange={set("q")} style={{ width: 220 }} data-testid="activity-filter-q" />
        <select className="input" value={filters.role} onChange={set("role")} style={{ width: 150 }} data-testid="activity-filter-role">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="input" value={filters.entity} onChange={set("entity")} style={{ width: 160 }} data-testid="activity-filter-entity">
          <option value="">All areas</option>
          {data.entities.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="input" value={filters.method} onChange={set("method")} style={{ width: 130 }} data-testid="activity-filter-method">
          <option value="">All types</option>
          <option value="POST">Create (POST)</option>
          <option value="PUT">Update (PUT)</option>
          <option value="PATCH">Update (PATCH)</option>
          <option value="DELETE">Delete</option>
          <option value="GET">Export (GET)</option>
        </select>
        <select className="input" value={filters.outcome} onChange={set("outcome")} style={{ width: 120 }} data-testid="activity-filter-outcome">
          <option value="">Any result</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
        <input className="input" type="date" value={filters.start} onChange={set("start")} style={{ width: 150 }} title="From date" data-testid="activity-filter-start" />
        <input className="input" type="date" value={filters.end} onChange={set("end")} style={{ width: 150 }} title="To date" data-testid="activity-filter-end" />
        <button type="submit" className="btn btn-gold btn-sm" data-testid="activity-filter-apply">Filter</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFilters(EMPTY_ACTIVITY_FILTERS); apply(EMPTY_ACTIVITY_FILTERS); }} data-testid="activity-filter-clear">Clear</button>
      </form>
      {error && <div className="pad-16 text-red fs-12">{error}</div>}
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Target</th><th>Result</th><th>IP</th></tr></thead>
          <tbody>
            {loading && data.items.length === 0 && <tr><td colSpan="6" className="text-muted text-center pad-16">Loading…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan="6" className="text-muted text-center pad-16">No activity matches these filters</td></tr>}
            {data.items.map((row) => <ActivityRow key={row.id} row={row} onOpen={setSelected} />)}
          </tbody>
        </table>
      </div>
      <AdminListPagination
        page={page}
        pages={data.pages}
        pageSize={pageSize}
        total={data.total}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        testId="activity"
        itemLabel="entries"
      />
      <ActivityDetailDrawer row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

// Per-user history, opened from Users → "Activity".
export function UserActivityDrawer({ user, onClose }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const label = user ? (`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email) : "";

  useEffect(() => {
    if (!user) return;
    setItems(null);
    api.get(`/admin/users/${user.id}/activity`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [user]);

  return (
    <>
      <AdminDetailDrawer
        open={!!user && !selected}
        onClose={onClose}
        title={`Activity — ${label}`}
        subtitle={user?.email}
        testId="user-activity-drawer"
      >
        <DrawerSection title="Actions by or on this user (latest 200)">
          {items === null && <p className="text-muted fs-12">Loading…</p>}
          {items && items.length === 0 && <p className="text-muted fs-12">No recorded activity yet.</p>}
          {items && items.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Time</th><th>Action</th><th>Target</th><th>Result</th><th>IP</th></tr></thead>
                <tbody>
                  {items.map((row) => <ActivityRow key={row.id} row={row} onOpen={setSelected} showActor={false} />)}
                </tbody>
              </table>
            </div>
          )}
        </DrawerSection>
      </AdminDetailDrawer>
      <ActivityDetailDrawer row={selected} onClose={() => setSelected(null)} />
    </>
  );
}

/* ───────────────────────────── Error Log ───────────────────────────── */

function StatusPill({ status }) {
  const cls = status === "resolved" ? "pill-green" : status === "regressed" ? "pill-amber" : "pill-red";
  const text = status === "resolved" ? "Resolved" : status === "regressed" ? "Back again" : "Open";
  return <span className={`pill ${cls}`} style={{ fontSize: 10 }}>{text}</span>;
}

const where = (g) => g.route || g.origin || (g.url ? g.url.replace(/^https?:\/\/[^/]+/, "") : "—");

function ErrorGroupDrawer({ group, onClose, onChanged, toast }) {
  const [occurrences, setOccurrences] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!group) return;
    setOccurrences(null);
    setNote(group.note || "");
    api.get(`/admin/error-logs?fingerprint=${encodeURIComponent(group.fingerprint)}`)
      .then((r) => setOccurrences(r.data.items || []))
      .catch(() => setOccurrences([]));
  }, [group]);

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === "resolve") await api.post(`/admin/error-logs/groups/${group.fingerprint}/resolve`, { note: note || null });
      else await api.post(`/admin/error-logs/groups/${group.fingerprint}/reopen`);
      toast?.(kind === "resolve" ? "Marked as resolved" : "Reopened");
      onChanged();
    } catch (e) {
      toast?.(formatApiError(e));
    }
    setBusy(false);
  };

  const latest = occurrences?.[0];
  return (
    <AdminDetailDrawer
      open={!!group}
      onClose={onClose}
      title={group ? (group.exc_type ? `${group.exc_type}: ${group.message}` : group.message) : ""}
      subtitle={group ? `${SOURCE_LABEL[group.source] || group.source} · ${where(group)}` : ""}
      badge={group && <StatusPill status={group.status} />}
      testId="error-drawer"
      footer={group && (
        <div className="flex gap-8" style={{ width: "100%", alignItems: "center" }}>
          {group.status === "resolved" ? (
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => act("reopen")} data-testid="error-reopen">Reopen</button>
          ) : (
            <>
              <input className="input" placeholder="Note (optional): what fixed it" value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1 }} data-testid="error-note" />
              <button className="btn btn-green btn-sm" disabled={busy} onClick={() => act("resolve")} data-testid="error-resolve">Mark resolved</button>
            </>
          )}
        </div>
      )}
    >
      {group && (
        <>
          <DrawerSection title="Summary">
            <DrawerFacts items={[
              ["Occurrences", group.count],
              ["Users affected", group.users],
              ["First seen", fmtTime(group.first_seen)],
              ["Last seen", fmtTime(group.last_seen)],
              ["Code location", group.frame || group.origin],
              group.resolved_at && ["Resolved", `${fmtTime(group.resolved_at)} by ${group.resolved_by || "—"}`],
              group.note && ["Note", group.note],
            ]} />
          </DrawerSection>
          {occurrences === null && <p className="text-muted fs-12">Loading occurrences…</p>}
          {latest && (
            <>
              <DrawerSection title="Latest occurrence">
                <DrawerFacts items={[
                  ["Time", fmtTime(latest.ts)],
                  ["User", latest.actor_email || latest.actor_id || "anonymous"],
                  ["Request", latest.method ? `${latest.method} ${latest.path || ""}` : null],
                  ["Page", latest.url],
                  ["Status", latest.status],
                  ["Request ID", latest.request_id],
                  ["IP", latest.ip],
                  ["Device", latest.user_agent],
                ]} />
              </DrawerSection>
              <DrawerSection title="Stack trace"><Json value={latest.stack || "No stack trace captured"} /></DrawerSection>
              {latest.component_stack && <DrawerSection title="React components"><Json value={latest.component_stack} /></DrawerSection>}
              {latest.body && <DrawerSection title="Data sent (secrets removed)"><Json value={latest.body} /></DrawerSection>}
            </>
          )}
          {occurrences && occurrences.length > 1 && (
            <DrawerSection title={`Recent occurrences (${occurrences.length})`}>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Time</th><th>User</th><th>Request ID</th></tr></thead>
                  <tbody>
                    {occurrences.map((o) => (
                      <tr key={o.id}>
                        <td className="fs-11 text-muted">{fmtTime(o.ts)}</td>
                        <td className="fs-12">{o.actor_email || "anonymous"}</td>
                        <td className="font-mono fs-11 text-muted">{o.request_id || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DrawerSection>
          )}
        </>
      )}
    </AdminDetailDrawer>
  );
}

const EMPTY_ERROR_FILTERS = { status: "open", source: "", q: "", start: "", end: "" };

export function AdminErrorLog({ toast }) {
  const [filters, setFilters] = useState(EMPTY_ERROR_FILTERS);
  const [applied, setApplied] = useState(EMPTY_ERROR_FILTERS);
  const [data, setData] = useState({ items: [], count: 0, open: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const p = new URLSearchParams();
    Object.entries(applied).forEach(([k, v]) => { if (v) p.set(k, v); });
    setLoading(true);
    setError("");
    api.get(`/admin/error-logs/groups?${p}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applied, reloadKey]);

  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });

  return (
    <div className="card" data-testid="admin-error-log">
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <div>
          <div className="card-title"> Error Log</div>
          <div className="fs-12 text-muted">Crashes and errors from the server and from users' browsers. Repeats of the same error are grouped.</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setReloadKey((k) => k + 1)} data-testid="error-refresh">Refresh</button>
      </div>
      <form
        className="flex gap-8 pad-16"
        style={{ flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        onSubmit={(e) => { e.preventDefault(); setApplied(filters); }}
      >
        <select className="input" value={filters.status} onChange={set("status")} style={{ width: 140 }} data-testid="error-filter-status">
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="">All</option>
        </select>
        <select className="input" value={filters.source} onChange={set("source")} style={{ width: 150 }} data-testid="error-filter-source">
          <option value="">All sources</option>
          <option value="backend">Backend crashes</option>
          <option value="backend.log">Server logs</option>
          <option value="frontend">Browser</option>
        </select>
        <input className="input" placeholder="Search message, route, request ID" value={filters.q} onChange={set("q")} style={{ width: 240 }} data-testid="error-filter-q" />
        <input className="input" type="date" value={filters.start} onChange={set("start")} style={{ width: 150 }} title="From date" data-testid="error-filter-start" />
        <input className="input" type="date" value={filters.end} onChange={set("end")} style={{ width: 150 }} title="To date" data-testid="error-filter-end" />
        <button type="submit" className="btn btn-gold btn-sm" data-testid="error-filter-apply">Filter</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFilters(EMPTY_ERROR_FILTERS); setApplied(EMPTY_ERROR_FILTERS); }} data-testid="error-filter-clear">Clear</button>
      </form>
      {error && <div className="pad-16 text-red fs-12">{error}</div>}
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Count</th><th>Last seen</th><th>Source</th><th>Error</th><th>Where</th><th>Users</th><th>Status</th></tr></thead>
          <tbody>
            {loading && data.items.length === 0 && <tr><td colSpan="7" className="text-muted text-center pad-16">Loading…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan="7" className="text-muted text-center pad-16">No errors match these filters</td></tr>}
            {data.items.map((g) => (
              <tr key={g.fingerprint} onClick={() => setSelected(g)} style={{ cursor: "pointer" }} data-testid={`error-row-${g.fingerprint}`}>
                <td className="fs-12" style={{ fontWeight: 600 }}>{g.count}×</td>
                <td className="fs-11 text-muted" style={{ whiteSpace: "nowrap" }}>{fmtTime(g.last_seen)}</td>
                <td className="fs-12">{SOURCE_LABEL[g.source] || g.source}</td>
                <td className="fs-12" style={{ maxWidth: 380 }}>
                  {g.exc_type && <span className="font-mono">{g.exc_type}: </span>}
                  <span style={{ wordBreak: "break-word" }}>{(g.message || "").slice(0, 160)}</span>
                </td>
                <td className="font-mono fs-11 text-muted" style={{ maxWidth: 240, wordBreak: "break-all" }}>{where(g)}</td>
                <td className="fs-12">{g.users || "—"}</td>
                <td><StatusPill status={g.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ErrorGroupDrawer
        group={selected}
        toast={toast}
        onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); setReloadKey((k) => k + 1); }}
      />
    </div>
  );
}
